import translations from './translations';

const defaultLanguage = 'en';

// Mapping of country codes to preferred languages
const countryToLanguageMap: { [key: string]: string } = {
    // Spanish-speaking countries
    AR: 'es',
    BO: 'es',
    CL: 'es',
    CO: 'es',
    CR: 'es',
    DO: 'es',
    EC: 'es',
    SV: 'es',
    GT: 'es',
    HN: 'es',
    MX: 'es',
    NI: 'es',
    PA: 'es',
    PY: 'es',
    PE: 'es',
    ES: 'es',
    UY: 'es',
    VE: 'es',

    // Portuguese-speaking countries
    BR: 'pt',
    PT: 'pt',
    AO: 'pt',
    MZ: 'pt',

    // French-speaking countries
    FR: 'fr',
    // CA removed to avoid forcing fr in multilingual country
    // BE removed to avoid forcing fr in multilingual country
    // CH removed to avoid forcing fr in multilingual country
    SN: 'fr',
    CI: 'fr',

    // Turkish-speaking countries
    TR: 'tr',
    CY: 'tr',

    // Arabic-speaking countries
    SA: 'ar',
    AE: 'ar',
    EG: 'ar',
    JO: 'ar',
    LB: 'ar',
    SY: 'ar',
    IQ: 'ar',
    KW: 'ar',
    QA: 'ar',
    BH: 'ar',
    OM: 'ar',
    YE: 'ar',
    MA: 'ar',
    TN: 'ar',
    DZ: 'ar',
    LY: 'ar',
    SD: 'ar',

    // Chinese-speaking countries/regions
    CN: 'zh',
    TW: 'zh',
    HK: 'zh',
    MO: 'zh',
    SG: 'zh'
};

// Get the list of supported languages from translations
const supportedLanguages = Object.keys(translations);

/**
 * Reads the optional lang query parameter and normalizes it to a supported language, if present.
 */
function getLanguageFromQueryParam(): string | undefined {
    try {
        if (typeof window === 'undefined' || !window.location || !window.location.search) return undefined;
        const params = new URLSearchParams(window.location.search);
        const langParam = params.get('lang');
        if (!langParam) return undefined;
        const normalized = langParam.toLowerCase();
        // Try exact match first
        if (supportedLanguages.includes(normalized)) return normalized;
        // Then try language-only portion (e.g., pt-BR -> pt)
        const base = normalized.split('-')[0];
        if (supportedLanguages.includes(base)) return base;
        return undefined;
    } catch (_) {
        return undefined;
    }
}

/**
 * Detects the user's preferred language based on browser locale
 * @returns {string} The detected language code or default language
 */
function detectBrowserLanguage(): string {
    // Try to get browser language
    const browserLanguage = typeof navigator !== 'undefined' ? navigator.language || (navigator as any).userLanguage : undefined;

    if (browserLanguage) {
        // Extract language code (e.g., 'en-US' -> 'en', 'pt-BR' -> 'pt')
        const languageCode = browserLanguage.split('-')[0].toLowerCase();

        // Check if we support this language
        if (supportedLanguages.includes(languageCode)) {
            return languageCode;
        }

        // Special case: if browser is set to Chinese variants
        if (languageCode === 'zh' || browserLanguage.toLowerCase().includes('zh')) {
            return 'zh';
        }
    }

    // Get additional browser languages as fallback
    const languages = typeof navigator !== 'undefined' ? (navigator as any).languages || [] : [];
    for (const lang of languages) {
        const langCode = lang.split('-')[0].toLowerCase();
        if (supportedLanguages.includes(langCode)) {
            return langCode;
        }
    }

    return defaultLanguage;
}

/**
 * Gets language based on country code
 * @param {string} countryCode - Two-letter country code (e.g., 'BR', 'US')
 * @returns {string} The language code for the country or default language
 */
function getLanguageFromCountry(countryCode: string): string {
    if (!countryCode) return defaultLanguage;

    const upperCountryCode = countryCode.toUpperCase();
    const mappedLanguage = countryToLanguageMap[upperCountryCode];

    // Return mapped language if supported, otherwise default
    return mappedLanguage && supportedLanguages.includes(mappedLanguage) ? mappedLanguage : defaultLanguage;
}

/**
 * Determines the best language to use based on available information
 * Precedence: query param ?lang => country mapping => browser => default
 * @param {string} [countryCode] - Optional country code to help determine language
 * @returns {string} The determined language code
 */
function getPreferredLanguage(countryCode?: string): string {
    const queryLang = getLanguageFromQueryParam();
    if (queryLang) return queryLang;

    if (countryCode) {
        const countryLanguage = getLanguageFromCountry(countryCode);
        if (countryLanguage) {
            return countryLanguage;
        }
    }

    return detectBrowserLanguage();
}

/**
 * Gets a translation for the given code using automatic language detection
 * @param {string} code - The translation key
 * @param {string} [countryCode] - Optional country code to help determine language
 * @returns {string} The translated text or the key if translation not found
 */
export function getTranslation(code: string, countryCode?: string): string {
    const language = getPreferredLanguage(countryCode);

    // Try to get translation for detected language
    if (translations[language] && translations[language][code]) {
        return translations[language][code];
    }

    // Fallback to default language
    if (translations[defaultLanguage] && translations[defaultLanguage][code]) {
        return translations[defaultLanguage][code];
    }

    // If all else fails, return the code itself
    return code;
}

/**
 * Gets the current detected language (useful for debugging or UI display)
 * @param {string} [countryCode] - Optional country code to help determine language
 * @returns {string} The detected language code
 */
export function getCurrentLanguage(countryCode?: string): string {
    return getPreferredLanguage(countryCode);
}

/**
 * Gets all supported languages
 * @returns {string[]} Array of supported language codes
 */
export function getSupportedLanguages(): string[] {
    return supportedLanguages;
}
